import { TIER_FEATURES } from "./subscriptionFeatures";

/**
 * SUB-005 — single source of truth for subscription CARD bullets.
 *
 * The server already ships an admin-editable, per-package feature list on each
 * subscription package as `feature_splits` (Backend `FeatureSplit` model →
 * `FeatureSplitSerializer`: `{ id, text, is_highlighted, tooltip }`). The cards
 * historically ignored it and rendered a hardcoded `TIER_FEATURES` constant
 * instead, so the two drifted (SUB-005). This resolver makes the SERVER the
 * source of truth: when a package carries `feature_splits`, the card renders
 * those; the hardcoded `TIER_FEATURES` remains ONLY as a safety fallback for
 * packages the server sends without any splits (so a card never renders blank).
 *
 * Once the dev confirms `FeatureSplit` rows are populated & correct for every
 * live tier (see the SUB-002/SUB-004 data-dump ask), the fallback and
 * `subscriptionFeatures.js` can be deleted outright.
 */

/**
 * Map one server `FeatureSplit` onto the shape the card renderer expects
 * (`{ feature_description, tooltip }`). `tooltip` is a string when present,
 * else `false` (the renderer only shows the info icon for string tooltips).
 * `text` is admin-authored rich text and is rendered via the same
 * `dangerouslySetInnerHTML` path the hardcoded descriptions already used —
 * same trust level (admin, not end-user), no new sink introduced.
 */
export function mapFeatureSplit(split) {
  return {
    feature_description: split && typeof split.text === "string" ? split.text : "",
    tooltip: split && split.tooltip ? split.tooltip : false,
    is_highlighted: !split || split.is_highlighted !== false,
  };
}

/**
 * Resolve the bullet list a card should render.
 *
 * @param {Object} args
 * @param {Array|undefined} args.featureSplits - server `item.feature_splits`.
 * @param {number} args.subTier - the package `sub_tier` (0..3).
 * @returns {Array<{feature_description: string, tooltip: (string|false)}>}
 */
export function resolveCardFeatures({ featureSplits, subTier } = {}) {
  if (Array.isArray(featureSplits)) {
    const usable = featureSplits
      .filter((s) => s && typeof s.text === "string" && s.text.trim() !== "")
      .map(mapFeatureSplit);
    if (usable.length > 0) return usable;
  }
  const tier = TIER_FEATURES.find((t) => t.sub_tier === subTier);
  return tier ? tier.features : [];
}
